import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAdminWs, useAdminWsConnected } from '../context/AdminWsContext';
import { RefreshCw, Wifi, WifiOff, FileArchive, AlertTriangle, CheckCircle } from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('ru-RU').format(n || 0);

const FEED_ICONS = {
  device_online:     { icon: Wifi,        color: 'var(--green)',  label: 'Подключился' },
  device_offline:    { icon: WifiOff,     color: 'var(--red)',    label: 'Отключился'  },
  diagnostics_ready: { icon: FileArchive, color: 'var(--brand)',  label: 'Диагностика' },
};

export const Overview = () => {
  const { fetchAuthorized } = useAuth();
  const wsOk = useAdminWsConnected();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feed, setFeed]       = useState([]);

  const addFeed = useCallback((type, name) => {
    setFeed(prev => [{ type, name, ts: Date.now() }, ...prev].slice(0, 40));
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchAuthorized('/api/dashboard/summary');
      const d   = await res.json();
      if (d.success) setSummary(d.summary);
    } catch {}
    finally { setLoading(false); }
  }, [fetchAuthorized]);

  const handleWs = useCallback((msg) => {
    if (msg.event === 'device_online') {
      addFeed('device_online', msg.data.machine_name || msg.data.device_id);
      setSummary(p => p ? { ...p, online_devices: (p.online_devices || 0) + 1, offline_devices: Math.max(0, (p.offline_devices || 0) - 1) } : p);
    }
    if (msg.event === 'device_offline') {
      addFeed('device_offline', msg.data.device_id?.slice(0, 8));
      setSummary(p => p ? { ...p, online_devices: Math.max(0, (p.online_devices || 0) - 1), offline_devices: (p.offline_devices || 0) + 1 } : p);
    }
    if (msg.event === 'diagnostics_ready') {
      addFeed('diagnostics_ready', msg.data.file_name || msg.data.device_id);
    }
  }, [addFeed]);

  useAdminWs(handleWs);

  useEffect(() => {
    loadData();
    const t = setInterval(loadData, 60_000);
    return () => clearInterval(t);
  }, [loadData]);

  const s = summary;

  const stats = [
    { label: 'Всего киосков',   value: s?.total_devices   ?? '—', color: null },
    { label: 'Онлайн',          value: s?.online_devices  ?? '—', color: 'var(--green)'  },
    { label: 'Оффлайн',         value: s?.offline_devices ?? '—', color: s?.offline_devices > 0 ? 'var(--red)' : null },
    { label: 'Листов всего',    value: fmt(s?.total_prints_all_time), color: null },
    { label: 'Заказов сегодня', value: s?.today_orders    ?? '—', color: null },
    { label: 'Выручка сегодня', value: `${fmt(s?.today_revenue)} ₽`, color: 'var(--yellow)' },
  ];

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Обзор</h1>
          <p>Статус сети в реальном времени</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span className={`badge ${wsOk ? 'badge-green' : 'badge-yellow'}`}>
            <span className={`dot ${wsOk ? 'dot-online' : 'dot-warning'}`} style={{ width: '6px', height: '6px' }} />
            {wsOk ? 'Live' : 'Подключение...'}
          </span>
          <button className="btn-icon" onClick={loadData} disabled={loading} title="Обновить">
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="stat-grid">
        {stats.map(({ label, value, color }) => (
          <div key={label} className="card stat-card">
            <div className="stat-value" style={color ? { color } : {}}>
              {value}
            </div>
            <div className="stat-label">{label}</div>
          </div>
        ))}
      </div>

      {/* Bottom row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '14px' }}>

        {/* Alerts */}
        <div className="card" style={{ padding: '20px' }}>
          <p style={{ fontSize: '13px', fontWeight: '600', marginBottom: '14px' }}>Уведомления</p>
          {s?.paper_low_count > 0 ? (
            <div className="alert alert-warning">
              <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: '1px' }} />
              <div>
                <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '2px' }}>Мало бумаги</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>
                  У {s.paper_low_count} устройства(ств) заканчивается бумага
                </div>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--green)', fontSize: '13px', padding: '8px 0' }}>
              <CheckCircle size={15} />
              Критических уведомлений нет
            </div>
          )}
        </div>

        {/* Live feed */}
        <div className="card" style={{ padding: '20px' }}>
          <p style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '7px' }}>
            <span className="dot dot-online" />
            Live события
          </p>
          {feed.length === 0 ? (
            <div className="empty" style={{ padding: '16px 0' }}>Ожидание событий...</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              {feed.map((ev, i) => {
                const meta = FEED_ICONS[ev.type] || { icon: null, color: 'var(--text-2)', label: ev.type };
                const Icon = meta.icon;
                return (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.05)',
                    fontSize: '12px',
                  }}>
                    {Icon && <Icon size={11} style={{ color: meta.color, flexShrink: 0 }} />}
                    <span className="truncate" style={{ flex: 1, color: 'var(--text-1)' }}>{ev.name}</span>
                    <span className="text-dim" style={{ fontSize: '10px', flexShrink: 0 }}>
                      {new Date(ev.ts).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
